import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

// ---------------------------------------------------------------------------
// SuccessScreen
//
// Positive-reinforcement close for the student-facing flow. The student
// sees a warm "great job" message, a count of the goals they rated, and
// two actions: run another check-in (for a different student) or exit
// (for the adult to close the session).
//
// Deliberately positive regardless of what the student rated. A student
// who honestly says "not today" for every goal is doing exactly what
// this feature exists to encourage — self-awareness — and shouldn't be
// punished with a sad UI.
// ---------------------------------------------------------------------------

const FEELING_LABELS = {
  blue: { label: 'Blue', emoji: '💧' },
  green: { label: 'Green', emoji: '🌱' },
  yellow: { label: 'Yellow', emoji: '☀️' },
  red: { label: 'Red', emoji: '🔥' },
};

const SuccessScreen = ({
  student,
  ratedGoalCount,
  feeling,
  onCheckInAgain,
  onExit,
}) => {
  const feelingSpec = feeling ? FEELING_LABELS[feeling] : null;
  const firstName = student?.firstName || 'friend';

  return (
    <div className="text-center py-6">
      {/* Trophy / celebration icon */}
      <div className="inline-flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 shadow-xl mb-6">
        <Icon
          name="Trophy"
          size={48}
          className="text-white drop-shadow"
        />
      </div>

      <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
        Great job, {firstName}!
      </h1>
      <p className="text-lg text-gray-600 mb-6 max-w-md mx-auto">
        {ratedGoalCount === 0
          ? "Thanks for showing up today. Every check-in matters."
          : ratedGoalCount === 1
            ? "You checked in on one of your goals. That's real self-awareness — nice work."
            : `You checked in on ${ratedGoalCount} of your goals. That's real self-awareness — nice work.`}
      </p>

      {/* Summary chips */}
      <div className="inline-flex flex-wrap items-center justify-center gap-2 mb-8">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-sm font-medium text-emerald-800">
          <Icon name="Check" size={14} />
          Saved to your teacher
        </span>
        {feelingSpec && (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5 text-sm font-medium text-blue-800">
            <span aria-hidden="true">{feelingSpec.emoji}</span>
            Feeling: {feelingSpec.label}
          </span>
        )}
      </div>

      {/* Actions — one big primary for kids ("check in again" is
          normally for switching to another student), one small
          secondary for the adult ("done for now"). */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
        <Button
          size="lg"
          onClick={onCheckInAgain}
          fullWidth
          className="sm:w-auto"
        >
          <Icon name="RotateCcw" size={18} className="mr-2" />
          Another student
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={onExit}
          fullWidth
          className="sm:w-auto"
        >
          <Icon name="Home" size={18} className="mr-2" />
          All done
        </Button>
      </div>
    </div>
  );
};

export default SuccessScreen;
